/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, all, call, takeLatest } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { StoreState } from 'store/store';
import {
  ActionFail,
  ActionRequest,
  ActionSuccess,
  Asset,
  AssetOption,
  AssetUtils,
  AsyncActionStateNoData,
  Ducks,
  Program,
  XError,
} from '@wit/vtv-x-foundation-lib';
import { getBarOptions } from 'store/ducks/programInfo/actions/getOptions';

export enum ActionType {
  REQUEST = 'programInfo/bar/options',
  SUCCESS = 'programInfo/bar/options/SUCCESS',
  FAIL = 'programInfo/bar/options/FAIL',
}

// ACTIONS
type Options = ActionRequest<ActionType.REQUEST, Asset>;
type OptionsSuccess = ActionSuccess<ActionType.SUCCESS, AssetOption[]>;
type OptionsFail = ActionFail<ActionType.FAIL>;

type Actions = Options | OptionsFail | OptionsSuccess;

const Actions = {
  request: (asset: Asset): Options => ({
    type: ActionType.REQUEST,
    meta: asset,
  }),
  success: (options: AssetOption[]): OptionsSuccess => ({
    type: ActionType.SUCCESS,
    payload: options,
  }),
  fail: (error: XError): OptionsFail => ({
    type: ActionType.FAIL,
    error,
  }),
};

export const Creators = { request: Actions.request };

// SELECTORS
export const Selectors = {
  isProcessing: (state: StoreState): boolean => {
    return state.programInfo.bar.options.processing === true;
  },
  isProcessed: (state: StoreState): boolean => {
    return state.programInfo.bar.options.processed === true;
  },
  getError: (state: StoreState): XError | undefined => {
    return state.programInfo.bar.options.error;
  },
  getOptions: (state: StoreState): AssetOption[] => {
    return state.programInfo.bar.options.data;
  },
};

// SAGAS
function* requestOptions(action: Options): SagaIterator {
  let asset: Asset | undefined = action.meta;
  const entryAssetMediaType = asset.mediaType;

  if (asset && AssetUtils.isChannel(asset)) {
    const curProgram: Program | undefined = yield call(Ducks.Epg.Live.getLiveProgram, asset.epgId);
    if (curProgram) {
      asset = curProgram;
    }
  }

  if (!asset) {
    yield put(Actions.fail(XError.fromError('content not found')));
    return;
  }

  yield all([call(Ducks.BookmarksCache.loadBookmarks, [asset])]);

  const options: AssetOption[] = yield call(getBarOptions, entryAssetMediaType, asset);

  yield put(Actions.success(options));
}

export function* saga(): SagaIterator {
  yield all([takeLatest(ActionType.REQUEST, requestOptions)]);
}

// REDUCER
export interface ReducerState extends AsyncActionStateNoData {
  data: AssetOption[];
}

const initialState: ReducerState = {
  processing: false,
  processed: false,
  error: undefined,
  data: [],
};

export default function PIBarOptions(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.REQUEST: {
      return {
        processing: true,
        processed: false,
        error: undefined,
        data: [],
      };
    }
    case ActionType.SUCCESS: {
      return {
        processing: false,
        processed: true,
        error: undefined,
        data: action.payload,
      };
    }
    case ActionType.FAIL: {
      return {
        processing: false,
        processed: true,
        error: action.error,
        data: [],
      };
    }
    default:
      return state;
  }
}
